/**
 * @since 2018/10/22
 * @author fanjingjian
 */
import { notification } from 'antd';
import { produce } from 'immer';
import {
  fetchMyTodoDetail,
  fetchRecordAudit,
  fetchMyTodo,
  fetchMyNewShops,
  submitAppealInfo,
  submitNotice,
  submitAuditInfo,
  fetchMyGroups,
  submitGiveUpQuote,
  fetchMyNewRecords,
  fetchBasicRecordDetail,
  submitNewAppealInfo,
  fetchQualificationStructure,
  submitTodo,
  submitAccept,
  submitRefuse,
} from '@/services/api';
import {
  REPORT_TYPE,
  FILTER_TYPE,
  REPORT_TYPE_LIST,
  AUDIT_TYPE_LIST,
  API_REQUEST_STATUS,
} from '@/configs/info';

const submitFaileHandler = ({ code, msg = '', data = '' }) => {
  notification.error({
    message: '提交失败',
    description: msg,
    duration: 0,
  });
};
const errorHandler = async ({ code, msg = '', data = {} }) => {
  notification.error({
    message: '网络请求失败',
    description: msg,
    duration: 0,
  });
};
const FILTERS = {
  'my-report-filter': FILTER_TYPE.ALL,
  'my-todo-filter': FILTER_TYPE.WAITING,
  'my-newshops-filter': FILTER_TYPE.WAITING,
};
export default {
  namespace: 'info',

  state: {
    'my-report-type': REPORT_TYPE_LIST.BASIC, // 默认是 『基本信息』报错
    'my-todo-type': AUDIT_TYPE_LIST.NEW, // 新老待办对应不同的 version 参数
    ...FILTERS,
    myRecords: {},
    recordAudit: {}, // 报错记录审核流程
    myTodo: {},
    tempMyTodoDetail: {},
    myNewShops: {},
    myGroups: {},
    showAppeal: false, // 控制申诉弹窗是否展开
    appealInfo: {}, // 申诉时填写的数据
    showAuditModal: false, // 待办界面弹窗开关
    auditInfo: {}, // 待办界面 举证 或 仲裁的提交信息
    recordsDisabledErrorItemIdList: [], // 我的报错，已提交申诉的 id 记录
    auditDisabledErrorItemIdList: [], // 我的待办，已提交的 id 记录
    currentErrorDetail: null, // 当前报错信息
    qualificationStructure: [],
  },

  effects: {
    *fetchMyRecords({ type, payload }, { call, put, select }) {
      const response = yield call(fetchMyNewRecords, {
        dataTypes: REPORT_TYPE[payload.type],
        status: API_REQUEST_STATUS[payload.status],
        currentPage: payload.currentPage + 1, // 基本信息页码从1开始，而之前是从0开始，这里特殊处理下页码
        pageSize: payload.pageSize,
      });
      // 基本信息页码从1开始，而之前是从0开始，这里特殊处理下页码
      response.currentPage -= 1;
      const currentType = yield select(state => state.info['my-report-type']);
      // 修复请求还未返回就提前切换了类型，导致列表渲染错误
      if (payload.type === currentType) {
        if (response) {
          // 将 用户输入的搜索关键词，放到 searchFilter 的 key 字段
          yield put({
            type: 'setMyRecords',
            payload: response,
          });
        } else {
          yield put({
            type: 'setMyRecords',
            payload: {
              currentPage: 0,
              pageSize: 10,
              totalPage: 0,
              totalItems: 0,
              data: [],
            },
          });
        }
      }
      // 清空缓存的 已申诉数据（后台会自动改变已被申诉的数据所在队列）
      yield put({
        type: 'setRecordsDisabledErrorItemIdList',
        payload: [],
      });
    },
    *fetchBasicRecordDetail({ payload }, { call, put }) {
      yield put({
        type: 'setField',
        payload: {
          recordAudit: {},
        },
      });
      const response = yield call(fetchBasicRecordDetail, payload, errorHandler);
      yield put({
        type: 'setField',
        payload: {
          recordAudit: response || {},
        },
      });
    },
    *fetchBasicAppealRecordDetail({ payload }, { call, put }) {
      yield put({
        type: 'setField',
        payload: {
          appealInfo: {},
        },
      });
      const response = yield call(fetchBasicRecordDetail, payload, errorHandler);
      yield put({
        type: 'setField',
        payload: {
          appealInfo: response || {},
        },
      });
    },
    *fetchRecordAudit({ payload }, { call, put }) {
      const response = yield call(fetchRecordAudit, payload);
      if (response && response.flowLogData) {
        yield put({
          type: 'setRecordAudit',
          payload: response.flowLogData,
        });
      } else {
        yield put({
          type: 'setRecordAudit',
          payload: {},
        });
      }
    },
    *fetchMyTodoDetail({ payload }, { call, put }) {
      const { noTemp = false, ...restParams } = payload;
      const response = yield call(fetchMyTodoDetail, restParams);
      if (!noTemp) {
        yield put({
          type: 'setMyTodoDetail',
          payload: response,
        });
      }
      return response;
    },
    *fetchMyTodo({ payload }, { call, put }) {
      const response = yield call(fetchMyTodo, payload);
      if (response) {
        // 将 用户输入的搜索关键词，放到 searchFilter 的 key 字段
        yield put({
          type: 'setMyTodo',
          payload: response,
        });
      } else {
        yield put({
          type: 'setMyTodo',
          payload: {
            currentPage: 0,
            pageSize: 10,
            totalPage: 0,
            totalItems: 0,
            data: [],
          },
        });
      }
      // 清空缓存的 已提交数据（后台会自动改变已被申诉的数据所在队列）
      yield put({
        type: 'setAuditDisabledErrorItemIdList',
        payload: [],
      });
    },
    *fetchMyNewShops({ type, payload }, { call, put }) {
      const response = yield call(fetchMyNewShops, {
        dataTypes: 101,
        ...payload,
      });
      if (response) {
        if (response.code === 901) {
          yield put({
            type: 'user/permissionReject',
            payload: {
              code: 901,
            },
          });
          yield put({
            type: 'setMyNewShops',
            payload: {
              currentPage: 0,
              pageSize: 10,
              totalPage: 0,
              totalItems: 0,
              data: [],
            },
          });
        } else {
          // 将 用户输入的搜索关键词，放到 searchFilter 的 key 字段
          yield put({
            type: 'setMyNewShops',
            payload: response.data,
          });
        }
      } else {
        yield put({
          type: 'setMyNewShops',
          payload: {
            currentPage: 0,
            pageSize: 10,
            totalPage: 0,
            totalItems: 0,
            data: [],
          },
        });
      }
      // 清空缓存的 已申诉数据（后台会自动改变已被申诉的数据所在队列）
      yield put({
        type: 'setRecordsDisabledErrorItemIdList',
        payload: [],
      });
    },
    *fetchMyGroups({ type, payload }, { call, put }) {
      const response = yield call(fetchMyGroups, payload);
      if (response) {
        // 将 用户输入的搜索关键词，放到 searchFilter 的 key 字段
        yield put({
          type: 'setMyGroups',
          payload: response,
        });
      } else {
        yield put({
          type: 'setMyGroups',
          payload: {
            currentPage: 0,
            pageSize: 10,
            totalPage: 0,
            totalItems: 0,
            data: [],
          },
        });
      }
      // 清空缓存的 已申诉数据（后台会自动改变已被申诉的数据所在队列）
      yield put({
        type: 'setRecordsDisabledErrorItemIdList',
        payload: [],
      });
    },
    *submitNewAppealInfo({ payload }, { call, put }) {
      const response = yield call(submitNewAppealInfo, payload, submitFaileHandler);
      if (response) {
        notification.success({
          message: '申诉提交成功',
          duration: 3,
        });
        return true;
      }
      return false;
    },
    *submitAppealInfo({ payload }, { call, put }) {
      const { id } = payload;
      const response = yield call(submitAppealInfo, payload, submitFaileHandler);
      if (response) {
        yield put({
          type: 'setField',
          payload: {
            appealInfo: {},
          },
        });
        yield put({
          type: 'setShowAppeal',
          payload: false,
        });
        // 暂时将该条置为灰色，防止不刷新列表重复提交申诉
        yield put({
          type: 'setRecordsDisabledErrorItemIdList',
          payload: String(id),
        });

        notification.success({
          message: '申诉提交成功',
          duration: 3,
        });
      }
    },
    *submitNotice({ payload }, { call, put }) {
      const { fetchQuery } = payload;
      const response = yield call(submitNotice, payload, submitFaileHandler);
      if (response === null) {
        // 清除举证数据
        yield put({
          type: 'setAuditInfo',
          payload: {
            comment: '',
            picUrls: [],
          },
        });
        yield put({
          type: 'setShowAuditModal',
          payload: false,
        });

        notification.success({
          message: '您已完成待办，请保证商家已知晓',
          duration: 3,
        });
        //之前是暂时将该条置为灰色，现在刷新当前页面的数据
        yield put({
          type: 'fetchMyTodo',
          payload: fetchQuery,
        });
      }
    },
    *submitTodo({ payload }, { call, put }) {
      const { fetchQuery } = payload;
      const response = yield call(submitTodo, payload, submitFaileHandler);
      if (response) {
        notification.success({
          message: '待办已完成',
          duration: 3,
        });
        // 刷新当前页面的数据
        yield put({
          type: 'fetchMyTodo',
          payload: fetchQuery,
        });
      }
    },
    *submitAuditInfo({ payload }, { call, put }) {
      const { fetchQuery } = payload;
      const response = yield call(submitAuditInfo, payload, submitFaileHandler);
      if (response === null) {
        // 清除举证数据
        yield put({
          type: 'setAuditInfo',
          payload: {
            comment: '',
            picUrls: [],
          },
        });
        yield put({
          type: 'setShowAuditModal',
          payload: false,
        });

        notification.success({
          message: '提交成功',
          duration: 3,
        });
        //之前是暂时将该条置为灰色，现在刷新当前页面的数据
        yield put({
          type: 'fetchMyTodo',
          payload: fetchQuery,
        });
      }
    },
    *submitGiveUpQuote({ payload }, { call, put, select }) {
      const fetchQuery =
        payload.fetchQuery ||
        (yield select(state => ({
          type: state.info['my-todo-filter'],
          currentPage: state.info.myTodo.currentPage,
          pageSize: state.info.myTodo.pageSize,
        })));
      const response = yield call(submitGiveUpQuote, payload, submitFaileHandler);
      if (response === null) {
        notification.success({
          message: '提交成功',
          duration: 3,
        });
        //刷新当前页面的数据
        yield put({
          type: 'fetchMyTodo',
          payload: fetchQuery,
        });
      }
    },
    *submitAccept({ payload }, { call, put, select }) {
      const fetchQuery =
        payload.fetchQuery ||
        (yield select(state => ({
          type: state.info['my-todo-filter'],
          currentPage: state.info.myTodo.currentPage,
          pageSize: state.info.myTodo.pageSize,
        })));
      const response = yield call(submitAccept, payload, submitFaileHandler);
      if (response) {
        notification.success({
          message: '提交成功',
          duration: 3,
        });
        //刷新当前页面的数据
        yield put({
          type: 'fetchMyTodo',
          payload: fetchQuery,
        });
      }
    },
    *submitRefuse({ payload }, { call, put, select }) {
      const fetchQuery =
        payload.fetchQuery ||
        (yield select(state => ({
          type: state.info['my-todo-filter'],
          currentPage: state.info.myTodo.currentPage,
          pageSize: state.info.myTodo.pageSize,
        })));
      const response = yield call(submitRefuse, payload, submitFaileHandler);
      if (response) {
        notification.success({
          message: '提交成功',
          duration: 3,
        });
        //刷新当前页面的数据
        yield put({
          type: 'fetchMyTodo',
          payload: fetchQuery,
        });
      }
    },
    *fetchQualificationStructure({ payload }, { call, select, put }) {
      const data = yield call(
        fetchQualificationStructure,
        { ...payload, method: 'GET' },
        'auditTask',
        ({ code, msg = '', data = '' }) => {
          console.error(code, msg, data);
        }
      );
      yield put({
        type: 'setField',
        payload: {
          qualificationStructure: data ? data.qualificationStructures : [],
        },
      });
    },
    *submitBrandAuditInfo({ payload }, { call, put }) {
      const { taskId, content, verifyAction, callback } = payload;
      const response = yield call(
        submitTodo,
        {
          body: {
            taskId,
            reaction: verifyAction?.type,
            content,
          },
        },
        submitFaileHandler
      );
      const res = response;
      if (res?.code === 200) {
        // 清除举证数据
        yield put({
          type: 'setAuditInfo',
          payload: {
            comment: '',
            picUrls: [],
          },
        });
        // 显示成功提示
        notification.success({
          message: '提交成功',
          duration: 3,
        });
        // 调用回调关闭弹窗
        if (typeof callback === 'function') {
          callback();
        }
        // 提交成功后，切换到已处理标签页并刷新列表
        yield put({
          type: 'setFilter',
          payload: { 'my-todo-filter': 'HANDLED' },
        });
        yield put({
          type: 'fetchMyTodo',
          payload: { type: 'HANDLED', currentPage: 0, pageSize: 10 },
        });
      }
    },
  },

  reducers: {
    setMyRecords(state, action) {
      const { payload } = action;
      return {
        ...state,
        myRecords: payload,
      };
    },
    setRecordAudit(state, action) {
      const { payload } = action;
      return {
        ...state,
        recordAudit: payload,
      };
    },
    setAppealInfo(state, action) {
      const { payload } = action;
      return {
        ...state,
        appealInfo: {
          ...state.appealInfo,
          ...payload,
        },
      };
    },
    setAuditInfo(state, action) {
      const { payload } = action;
      return {
        ...state,
        auditInfo: {
          ...state.auditInfo,
          ...payload,
        },
      };
    },
    setShowAppeal(state, action) {
      const { payload } = action;
      return {
        ...state,
        showAppeal: payload,
      };
    },
    setShowAuditModal(state, action) {
      const { payload } = action;
      return {
        ...state,
        showAuditModal: payload,
      };
    },
    setMyTodoDetail(state, action) {
      const { payload } = action;
      return {
        ...state,
        tempMyTodoDetail: payload,
      };
    },
    setMyTodo(state, action) {
      const { payload } = action;
      return {
        ...state,
        myTodo: payload,
      };
    },
    setMyNewShops(state, action) {
      const { payload } = action;
      return {
        ...state,
        myNewShops: payload,
      };
    },
    setMyGroups(state, action) {
      const { payload } = action;
      return {
        ...state,
        myGroups: payload,
      };
    },
    setFilter(state, action) {
      const { payload } = action;
      return {
        ...state,
        ...payload,
      };
    },
    clearFilters(state) {
      return {
        ...state,
        ...FILTERS,
      };
    },
    setReportType(state, action) {
      const { payload } = action;
      return {
        ...state,
        ...payload,
      };
    },

    setCurrentErrorDetail(state, action) {
      const { payload } = action;
      return {
        ...state,
        currentErrorDetail: payload,
      };
    },

    setRecordsDisabledErrorItemIdList(state, action) {
      const { recordsDisabledErrorItemIdList } = state;
      const { payload } = action;
      if (!payload || payload.length === 0) {
        return {
          ...state,
          recordsDisabledErrorItemIdList: [],
        };
      } else {
        return {
          ...state,
          recordsDisabledErrorItemIdList: [...recordsDisabledErrorItemIdList, payload],
        };
      }
    },
    setAuditDisabledErrorItemIdList(state, action) {
      const { auditDisabledErrorItemIdList } = state;
      const { payload } = action;
      if (!payload || payload.length === 0) {
        return {
          ...state,
          auditDisabledErrorItemIdList: [],
        };
      } else {
        return {
          ...state,
          auditDisabledErrorItemIdList: [...auditDisabledErrorItemIdList, payload],
        };
      }
    },
    setField(state, action) {
      return {
        ...state,
        ...action.payload,
      };
    },
    updateCancelText(state, { payload }) {
      return produce(state, draft => {
        const { referId, dpShopId, cancelText } = payload;
        const target = draft.myNewShops.data.find(
          item => item.referId === referId && item.dpShopId === dpShopId
        );
        if (target) target.auxFunc.cancelText = cancelText;
      });
    },
  },
};
